import React from 'react';
import { Box, Typography } from '@mui/material';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';

interface Props {
    suggestions: string[];
    onSelect: (question: string) => void;
    variant?: 'chips' | 'list';
}

const SuggestedQuestions: React.FC<Props> = ({
    suggestions,
    onSelect,
    variant = 'chips',
}) => {
    if (!suggestions.length) return null;

    return variant === 'list'
        ? <SuggestionList suggestions={suggestions} onSelect={onSelect} />
        : <SuggestionChips suggestions={suggestions} onSelect={onSelect} />;
};

// ── Chips — used as follow-up suggestions after a response ───────────

interface SuggestionChipsProps {
    suggestions: string[];
    onSelect: (q: string) => void;
}

const SuggestionChips: React.FC<SuggestionChipsProps> = ({ suggestions, onSelect }) => (
    <Box
        sx={{
            px: 1.5,
            py: 0.875,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.6,
            flexShrink: 0,
        }}
    >
        <Typography
            sx={{
                width: '100%',
                fontSize: 10,
                fontWeight: 500,
                color: 'text.disabled',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                mb: 0.25,
            }}
        >
            Suggested
        </Typography>
        {suggestions.slice(0, 4).map(q => (
            <Chip key={q} label={q} onClick={() => onSelect(q)} />
        ))}
    </Box>
);

const Chip: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
    <Box
        onClick={onClick}
        sx={{
            fontSize: 11,
            px: 1.25,
            py: 0.5,
            borderRadius: '99px',
            border: '0.5px solid',
            borderColor: 'divider',
            color: 'text.secondary',
            cursor: 'pointer',
            lineHeight: 1.4,
            userSelect: 'none',
            transition: 'all 0.12s',
            '&:hover': {
                bgcolor: '#EEEDFE',
                borderColor: '#AFA9EC',
                color: '#3C3489',
            },
        }}
    >
        {label}
    </Box>
);

// ── List — used in the empty state ──────────────────────────────────

interface SuggestionListProps {
    suggestions: string[];
    onSelect: (q: string) => void;
}

const icons: Record<number, React.ReactNode> = {
    0: <span style={{ fontSize: 14 }}>£</span>,
    1: <TravelExploreOutlinedIcon sx={{ fontSize: 15 }} />,
    2: <span style={{ fontSize: 14 }}>⏱</span>,
    3: <span style={{ fontSize: 14 }}>📋</span>,
};

const SuggestionList: React.FC<SuggestionListProps> = ({ suggestions, onSelect }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
        {suggestions.slice(0, 5).map((q, i) => (
            <Box
                key={q}
                onClick={() => onSelect(q)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.25,
                    py: 0.875,
                    borderRadius: 1.5,
                    border: '0.5px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    fontSize: 13,
                    color: 'text.primary',
                    bgcolor: 'background.default',
                    transition: 'all 0.12s',
                    '&:hover': {
                        bgcolor: '#EEEDFE',
                        borderColor: '#AFA9EC',
                        color: '#3C3489',
                    },
                }}
            >
                <Box sx={{
                    width: 24, height: 24,
                    borderRadius: 1,
                    bgcolor: 'background.secondary',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'text.secondary', flexShrink: 0,
                }}>
                    {icons[i] ?? <TravelExploreOutlinedIcon sx={{ fontSize: 14 }} />}
                </Box>
                {q}
            </Box>
        ))}
    </Box>
);

export default SuggestedQuestions;